import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Cell,
  LabelList,
} from 'recharts';

interface BarChartData {
  name: string;
  [key: string]: string | number;
}

interface BarChartComponentProps {
  data: BarChartData[];
  dataKeys: { key: string; name: string; color: string }[];
  height?: number;
  title?: string;
  horizontal?: boolean;
  showLabelsOnBars?: boolean;
  showLegend?: boolean;
  onBarClick?: (data: any, index: number) => void;
  selectedItem?: string | null;
}

const BarChartComponent: React.FC<BarChartComponentProps> = ({
  data,
  dataKeys,
  height = 400,
  title,
  horizontal = false,
  showLabelsOnBars = false,
  showLegend = true,
  onBarClick,
  selectedItem,
}) => {
  const [isDark, setIsDark] = useState<boolean>(() => document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const check = () => setIsDark(document.documentElement.classList.contains('dark'));
    check();
    const observer = new MutationObserver(() => check());
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  const handleClick = (data: any, index: number) => {
    if (onBarClick) {
      onBarClick(data, index);
    }
  };
  // Calculate domain to ensure minimum bar width for labels
  const getXAxisDomain = (): [number, number | 'auto'] => {
    if (!horizontal || !showLabelsOnBars || data.length === 0) {
      return [0, 'auto'];
    }

    const maxValue = Math.max(...data.map(d => Number(d[dataKeys[0].key])));
    const minValue = Math.min(...data.map(d => Number(d[dataKeys[0].key])));

    // Ensure the smallest bar is at least 30% of the max value for label visibility
    const adjustedMax = Math.max(maxValue, minValue / 0.3);

    return [0, adjustedMax];
  };

  // Generate random colors for each bar
  const getRandomColor = (index: number) => {
    const colors = [
      '#3b82f6', // blue
      '#10b981', // green
      '#f59e0b', // amber
      '#ef4444', // red
      '#8b5cf6', // purple
      '#ec4899', // pink
      '#14b8a6', // teal
      '#f97316', // orange
      '#06b6d4', // cyan
      '#84cc16', // lime
      '#a855f7', // violet
      '#f43f5e', // rose
      '#0ea5e9', // sky
      '#22c55e', // emerald
      '#eab308', // yellow
      '#6366f1', // indigo
    ];
    return colors[index % colors.length];
  };

  const renderCustomLabel = (props: any) => {
    const { x, y, width, height, index } = props;

    if (horizontal && data[index]) {
      const barWidth = width || 0;
      const countValue = data[index][dataKeys[0].key];
      const topicName = data[index].name;

      // Only show label if bar is wide enough
      if (barWidth > 50) {
        // Estimate character width (roughly 6px per character at font size 11)
        const countText = String(countValue);
        const countWidth = countText.length * 7; // Space needed for count + padding
        const availableWidth = barWidth - countWidth - 15; // 15px total padding
        const estimatedCharsPerPixel = 6;
        const maxChars = Math.floor(availableWidth / estimatedCharsPerPixel);

        // Truncate topic name if it won't fit
        const displayName = topicName.length > maxChars && maxChars > 3
          ? topicName.substring(0, maxChars - 3) + '...'
          : topicName;

        return (
          <g>
            {/* Topic name on the left of bar */}
            <text
              x={x + 5}
              y={y + height / 2}
              fill="#ffffff"
              textAnchor="start"
              dominantBaseline="middle"
              fontSize="11"
              fontWeight="500"
            >
              {displayName}
            </text>
            {/* Count value at the end of bar */}
            <text
              x={x + width - 5}
              y={y + height / 2}
              fill="#ffffff"
              textAnchor="end"
              dominantBaseline="middle"
              fontSize="11"
              fontWeight="600"
            >
              {countValue}
            </text>
          </g>
        );
      }
    }
    return null;
  };

  return (
    <div className="bg-panel rounded-lg shadow p-6">
      {title && (
        <h3 className="text-lg font-semibold text-heading mb-4">
          {title}
        </h3>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          layout={horizontal ? 'vertical' : 'horizontal'}
          margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
          barCategoryGap="10%"
        >
          <CartesianGrid
            strokeDasharray="3 3"
            className="stroke-gray-200 dark:stroke-gray-700"
          />
          {horizontal ? (
            <>
              <XAxis
                type="number"
                hide={showLabelsOnBars}
                domain={getXAxisDomain()}
                className="text-gray-600 dark:text-gray-400"
                tick={{ fill: 'currentColor' }}
              />
              <YAxis
                type="category"
                dataKey="name"
                hide={showLabelsOnBars}
                width={showLabelsOnBars ? 0 : 150}
                className="text-gray-600 dark:text-gray-400"
                tick={{ fill: 'currentColor', fontSize: 12 }}
              />
            </>
          ) : (
            <>
              <XAxis
                dataKey="name"
                className="text-gray-600 dark:text-gray-400"
                tick={{ fill: 'currentColor' }}
              />
              <YAxis
                className="text-gray-600 dark:text-gray-400"
                tick={{ fill: 'currentColor' }}
              />
            </>
          )}
          <Tooltip
            contentStyle={{
              backgroundColor: 'rgb(31 41 55)',
              border: 'none',
              borderRadius: '0.5rem',
              color: '#ffffff',
            }}
            itemStyle={{
              color: '#ffffff',
            }}
            labelStyle={{
              color: '#ffffff',
            }}
          />
          {showLegend && <Legend />}
          {dataKeys.map((item) => (
            <Bar
              key={item.key}
              dataKey={item.key}
              name={item.name}
              radius={horizontal ? [0, 8, 8, 0] : [8, 8, 0, 0]}
              onClick={handleClick}
              cursor={onBarClick ? 'pointer' : 'default'}
            >
              {data.map((entry, index) => {
                const isSelected = selectedItem && entry.name.toLowerCase() === selectedItem.toLowerCase();
                return (
                  <Cell
                    key={`cell-${index}`}
                    fill={isSelected ? (isDark ? '#e5e7eb' : '#000000') : getRandomColor(index)}
                  />
                );
              })}
              {showLabelsOnBars && <LabelList dataKey="name" content={renderCustomLabel} />}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default BarChartComponent;
